/**
 * React hooks for guests
 * Integrates with TanStack Query for caching and state management
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import * as guestsService from '../services/guests';

// Query keys for consistent cache management
export const guestKeys = {
  all: ['guests'] as const,
  lists: () => [...guestKeys.all, 'list'] as const,
  list: (filters: {
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) => [...guestKeys.lists(), filters] as const,
  count: (days: number) => [...guestKeys.all, 'count', days] as const,
  countToday: () => [...guestKeys.all, 'count', 'today'] as const,
  export: (filters: { dateFrom?: string; dateTo?: string }) =>
    [...guestKeys.all, 'export', filters] as const,
};

const GUESTS_PER_PAGE = 20;

/**
 * Fetch paginated guest log with date filtering (Admin only)
 */
export function useGuestLog(options?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: guestKeys.list({
      dateFrom: options?.dateFrom,
      dateTo: options?.dateTo,
      limit: options?.limit,
      offset: options?.offset,
    }),
    queryFn: () => guestsService.getGuestsWithProfiles(options),
    staleTime: 2 * 60 * 1000, // 2 minutes - guest data is relatively fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
  });
}

/**
 * Fetch paginated guest log with infinite scroll support (Admin only)
 * Uses useInfiniteQuery to automatically accumulate pages and handle pagination
 */
export function useGuestLogInfinite(options?: {
  dateFrom?: string;
  dateTo?: string;
}) {
  return useInfiniteQuery({
    queryKey: guestKeys.list({
      dateFrom: options?.dateFrom,
      dateTo: options?.dateTo,
    }),
    queryFn: ({ pageParam }) =>
      guestsService.getGuestsWithProfiles({
        ...options,
        limit: GUESTS_PER_PAGE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === GUESTS_PER_PAGE
        ? allPages.length * GUESTS_PER_PAGE
        : undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes - guest data is relatively fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
  });
}

/**
 * Get count of guests registered in the last N days (Admin only)
 */
export function useGuestCount(days: number) {
  return useQuery({
    queryKey: guestKeys.count(days),
    queryFn: () => guestsService.getGuestCount(days),
    staleTime: 5 * 60 * 1000, // 5 minutes - count doesn't change frequently
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache
  });
}

/**
 * Get count of guests visiting today (Admin only)
 * Filters by visit_date matching today's date (not last 24 hours)
 */
export function useGuestCountToday() {
  return useQuery({
    queryKey: guestKeys.countToday(),
    queryFn: () => guestsService.getGuestCountToday(),
    staleTime: 5 * 60 * 1000, // 5 minutes - count doesn't change frequently
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache
  });
}

/**
 * Fetch all guests for export matching date filter (Admin only)
 * Note: enabled: false prevents auto-fetching - only fetches when refetch() is called
 */
export function useGuestsForExport(options?: {
  dateFrom?: string;
  dateTo?: string;
}) {
  return useQuery({
    queryKey: guestKeys.export({
      dateFrom: options?.dateFrom,
      dateTo: options?.dateTo,
    }),
    queryFn: () => guestsService.getAllGuestsForExport(options),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: false, // Only fetch when refetch() is explicitly called (on export button click)
  });
}

